import { parseGeocoderResults } from "./geo"

const PIN_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 46" width="36" height="46">
    <path d="M18 1C9 1 2 8 2 17c0 12 16 28 16 28s16-16 16-28C34 8 27 1 18 1z" fill="#059669" stroke="#fff" stroke-width="2"/>
    <circle cx="18" cy="17" r="6" fill="#fff"/>
  </svg>`

// Google's Maps JavaScript API is loaded once per page, on demand.
let loadPromise = null
function loadGoogleMaps(key) {
  if (window.google?.maps?.importLibrary) return Promise.resolve()
  loadPromise ||= new Promise((resolve, reject) => {
    window.__googleMapsReady = () => resolve()
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly&loading=async&region=IN&language=en&callback=__googleMapsReady`
    script.async = true
    script.onerror = () => {
      loadPromise = null
      reject(new Error("The Google Maps script could not be loaded"))
    }
    document.head.appendChild(script)
  })
  return loadPromise
}

// Google calls this global when the key is invalid, restricted to another site, or has no billing.
const authFailureHandlers = new Set()
window.gm_authFailure = () => authFailureHandlers.forEach((handler) => handler())

const toLiteral = (point) => (typeof point.lat === "function" ? { lat: point.lat(), lng: point.lng() } : { lat: point.lat, lng: point.lng })

// Google Maps + Places (New) + Geocoding. Autocomplete uses session tokens so one typing session plus the final
// choice is billed as a single session.
export default class GoogleProvider {
  cacheable = false // suggestions belong to a billing session, so never replay old ones

  constructor({ element, key, mapId, onAuthFailure }) {
    this.element = element
    this.key = key
    this.mapId = mapId || "DEMO_MAP_ID"
    this.onAuthFailure = onAuthFailure
  }

  async init({ center, zoom, marker }, { onTap, onDrag }) {
    this.onDrag = onDrag
    this.authHandler = () => this.onAuthFailure?.()
    authFailureHandlers.add(this.authHandler)

    await loadGoogleMaps(this.key)
    const { Map } = await google.maps.importLibrary("maps")
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker")
    this.places = await google.maps.importLibrary("places")
    const { Geocoder } = await google.maps.importLibrary("geocoding")
    this.AdvancedMarkerElement = AdvancedMarkerElement
    this.geocoder = new Geocoder()

    this.map = new Map(this.element, {
      center, zoom, mapId: this.mapId,
      clickableIcons: false, // tapping a shop icon should drop the pin, not open Google's info card
      streetViewControl: false, fullscreenControl: false, mapTypeControl: true,
      gestureHandling: "cooperative" // page scrolling still works on phones
    })
    this.map.addListener("click", (event) => onTap(toLiteral(event.latLng)))
    if (marker) this.setMarker(marker)
  }

  setMarker(position) {
    if (this.marker) {
      this.marker.position = position
      return
    }
    const content = document.createElement("div")
    content.innerHTML = PIN_SVG
    this.marker = new this.AdvancedMarkerElement({ map: this.map, position, gmpDraggable: true, content })
    this.marker.addListener("dragend", () => this.onDrag(toLiteral(this.marker.position)))
  }

  moveTo(position, zoom) {
    this.map.setCenter(position)
    this.map.setZoom(zoom)
  }

  resize() {
    if (!this.map) return
    const center = this.map.getCenter()
    google.maps.event.trigger(this.map, "resize")
    if (center) this.map.setCenter(center)
  }

  async suggest(query) {
    this.sessionToken ||= new this.places.AutocompleteSessionToken()
    const center = this.map.getCenter()
    const { suggestions } = await this.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: query,
      sessionToken: this.sessionToken,
      includedRegionCodes: ["in"],
      language: "en",
      // Prefer places near where the map is looking, so a short local name like "pbvn" finds the local one first
      locationBias: { center: toLiteral(center), radius: 50000 }
    })

    return suggestions
      .filter((suggestion) => suggestion.placePrediction)
      .slice(0, 6)
      .map(({ placePrediction }) => ({
        label: placePrediction.text?.text ?? String(placePrediction.text),
        resolve: async () => {
          const place = placePrediction.toPlace()
          await place.fetchFields({ fields: ["location"] })
          this.sessionToken = null // choosing a place ends the billing session
          return toLiteral(place.location)
        }
      }))
  }

  async reverse(position) {
    try {
      const { results } = await this.geocoder.geocode({ location: position })
      return parseGeocoderResults(results)
    } catch (error) {
      if (error?.code === "ZERO_RESULTS") return null
      throw error
    }
  }

  destroy() {
    authFailureHandlers.delete(this.authHandler)
    this.marker && (this.marker.map = null)
    this.marker = null
    this.map = null
    this.element.replaceChildren()
  }
}
